import { computed } from "vue";
import { useState } from "#imports";

const COMPARE_STORAGE_KEY = "compare_products";

const normalizeText = (value: unknown) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeCompareId = (value: unknown) =>
  normalizeText(value).replace(/[\s\u00A0]+/g, "");

const parsePrice = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const cleaned = value.replace(/[\s\u00A0]/g, "").replace(",", ".");
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export type CompareProductItem = {
  id: string;
  title: string;
  article: string;
  brand: string;
  price: number;
  discountPrice?: number;
  priceBadgeLabel?: string;
  image: string;
  unit?: string;
};

type CompareProductInput = {
  id: string | number;
  title?: string;
  article?: string;
  brand?: string;
  price?: number | string;
  discountPrice?: number | string;
  priceBadgeLabel?: string;
  image?: string;
  unit?: string;
};

const normalizeItem = (
  input: CompareProductInput | CompareProductItem | null | undefined,
): CompareProductItem | null => {
  const id = normalizeCompareId(input?.id);
  if (!id) return null;

  const title = normalizeText(input?.title);
  const article = normalizeText(input?.article);
  const brand = normalizeText(input?.brand);
  const image = normalizeText(input?.image);
  const unit = normalizeText(input?.unit);
  const priceBadgeLabel = normalizeText(input?.priceBadgeLabel);
  const price = parsePrice(input?.price);
  const discountPrice = parsePrice(input?.discountPrice);

  return {
    id,
    title,
    article,
    brand,
    price,
    discountPrice: discountPrice > 0 ? discountPrice : undefined,
    priceBadgeLabel: priceBadgeLabel || undefined,
    image,
    unit: unit || undefined,
  };
};

export const useCompare = () => {
  const items = useState<CompareProductItem[]>("compare:items", () => []);
  const hydrated = useState<boolean>("compare:hydrated", () => false);

  const hydrate = () => {
    if (!process.client || hydrated.value) return;

    try {
      const raw = localStorage.getItem(COMPARE_STORAGE_KEY);
      if (!raw) {
        items.value = [];
        hydrated.value = true;
        return;
      }

      const parsed = JSON.parse(raw);
      items.value = Array.isArray(parsed)
        ? parsed
            .map((item) => normalizeItem(item))
            .filter((item): item is CompareProductItem => Boolean(item))
        : [];
    } catch (error) {
      console.error("Не удалось загрузить список сравнения", error);
      items.value = [];
    } finally {
      hydrated.value = true;
    }
  };

  const persist = () => {
    if (!process.client) return;
    localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(items.value));
  };

  const ensureHydrated = () => {
    hydrate();
  };

  const isCompared = (id: string | number) => {
    ensureHydrated();
    const normalizedId = normalizeCompareId(id);
    return items.value.some((item) => item.id === normalizedId);
  };

  const addToCompare = (input: CompareProductInput) => {
    ensureHydrated();
    const normalized = normalizeItem(input);
    if (!normalized) return false;
    if (items.value.some((item) => item.id === normalized.id)) return true;

    items.value = [...items.value, normalized];
    persist();
    return true;
  };

  const removeFromCompare = (id: string | number) => {
    ensureHydrated();
    const normalizedId = normalizeCompareId(id);
    items.value = items.value.filter((item) => item.id !== normalizedId);
    persist();
  };

  const toggleCompare = (input: CompareProductInput) => {
    const normalizedId = normalizeCompareId(input.id);
    if (!normalizedId) return false;

    if (isCompared(normalizedId)) {
      removeFromCompare(normalizedId);
      return false;
    }

    addToCompare(input);
    return true;
  };

  const clearCompare = () => {
    ensureHydrated();
    items.value = [];
    persist();
  };

  if (process.client) {
    hydrate();
  }

  const count = computed(() => items.value.length);
  const ids = computed(() => items.value.map((item) => item.id));

  return {
    items,
    ids,
    count,
    isCompared,
    addToCompare,
    removeFromCompare,
    toggleCompare,
    clearCompare,
    ensureHydrated,
  };
};
